import { SelectOption } from 'ui-components/src/components/atoms/Select/types';
import {
  AdHocRegistrationValue,
  AmountRegistrationValue,
  CargoRegistrationValue,
  CustomPropertyValue,
  DateTimeRegistrationValue,
  GroupRegistrationValue,
  PassengerRegistrationValue,
  PeriodRegistrationValue,
  RegistrationOption,
  RegistrationOptionType,
  RegistrationType,
  RegistrationValue,
  SelectInputValue,
  ServiceRegistrationValue,
  StringInputValue,
  UtilityRegistrationValue,
} from '../../types';

// Extracts all default registration values from a given template
export function templateToDefaultRegistrations(
  template: RegistrationTemplate | undefined,
): RegistrationValue[] {
  return (
    template?.fields?.map((field) => fieldToDefaultValues(field)).flat() || []
  );
}

// Extracts all registration options from a given template
export function templateToRegistrationOptions(
  template: RegistrationTemplate | undefined,
): RegistrationOption[] {
  return (
    template?.fields
      ?.map((field) => fieldToRegistrationOptions(field))
      .flat() || []
  );
}

function fieldToDefaultValues(
  field: RegistrationField,
  parentName?: string,
): RegistrationValue[] {
  const fieldType = field.custom
    ? 'CustomProperty'
    : (field.type as RegistrationType);

  if (field.optional) return [];

  if (!getDefaultRegistrationValue[fieldType])
    throw new Error(`Field of type [${fieldType}] is not supported`);
  return getDefaultRegistrationValue[fieldType](field).map((value: any) => ({
    id: value.id || field.name,
    parentName,
    type: fieldType,
    name: field.name,
    label: field.label,
    optional: field.optional,
    value,
  }));
}

function fieldToRegistrationOptions(
  field: RegistrationField,
  groupName?: string,
): RegistrationOption[] {
  const fieldType = field.type as RegistrationOptionType;

  if (field.optional === false && field.fields?.length) {
    return field.fields
      .map((subfield) => fieldToRegistrationOptions(subfield, field.name))
      .flat();
  } else if (!field.optional) {
    return [];
  }

  if (!groupName)
    throw new Error(
      `No groupName was provided for optional field: ${JSON.stringify(field)}`,
    );
  if (!getDefaultRegistrationValue[fieldType])
    throw new Error(`Field of type [${fieldType}] is not supported`);
  return getDefaultRegistrationValue[fieldType](field).map((value: any) => ({
    type: fieldType,
    parentName: groupName,
    name: field.name,
    label: field.label,
    value,
  }));
}

// export function fieldToRegistrationOptions
export const getDefaultRegistrationValue: {
  [n in RegistrationType]: ExtractOptionsFn;
} = {
  Group: (field): GroupRegistrationValue['value'][] => {
    return [
      (field.fields || [])
        .map((subfield) => fieldToDefaultValues(subfield, field.name))
        .flat(),
    ];
  },

  SimpleGroup: (field): GroupRegistrationValue['value'][] => {
    return [
      (field.fields || [])
        .map((subfield) => fieldToDefaultValues(subfield, field.name))
        .flat(),
    ];
  },

  AdHocRegistration: (field): AdHocRegistrationValue['value'][] => {
    return [
      { name: field.name, amount: 0, accountNumber: '', description: '' },
    ];
  },

  ServiceRegistration: (field): ServiceRegistrationValue['value'][] => {
    return [{ name: field.name }];
  },

  AmountRegistration: (field): AmountRegistrationValue['value'][] => {
    return [{ name: field.name, description: '', amount: 0 }];
  },

  Utility: (field): UtilityRegistrationValue['value'][] => {
    return [
      {
        name: field.name,
        quantity: 0,
        startReading: 0,
        endReading: 0,
        unitType: field.unitType || '',
      },
    ];
  },

  CargoRegistration: (field): CargoRegistrationValue['value'][] => {
    return [
      {
        name: field.name,
        quantity: 0,
        unitType: '',
      },
    ];
  },

  VisitCargoRegistration: (field): CargoRegistrationValue['value'][] => {
    return [
      {
        name: field.name,
        quantity: 0,
        unitType: '',
        location: {},
        transferType: 'discharged',
      },
    ];
  },

  VisitPeriod: (): PeriodRegistrationValue['value'][] => {
    return [{ arrival: null, departure: null }];
  },

  DateTime: (): DateTimeRegistrationValue['value'][] => {
    return [{ isoDate: '' }];
  },

  CustomProperty: (field): CustomPropertyValue['value'][] => {
    return [
      {
        name: field.name,
        value: '',
        renderType: field.type,
        selectableValues: field.selectableValues || [],
        systemEnumType: field.systemEnumType,
      },
    ];
  },

  StringInput: (): StringInputValue['value'][] => {
    return [''];
  },

  SelectInput: (field): SelectInputValue['value'][] => {
    return [{ value: '', selectableValues: field.selectableValues || [] }];
  },

  NrOfPassengers: (field): PassengerRegistrationValue['value'][] => {
    return [
      { name: field.name, nrOfPassengers: 0, unitType: field.unitType || '' },
    ];
  },
};

type ExtractOptionsFn = (
  field: RegistrationField,
) => RegistrationValue['value'][];

interface RegistrationTemplate {
  fields?: RegistrationField[] | null;
}

interface RegistrationField {
  name: string;
  label: string;
  type: string;
  optional: boolean;
  unitType?: string | null;
  fields?: RegistrationField[] | null;
  custom?: boolean | null;
  selectableValues?: SelectOption[] | null;
  systemEnumType?: string | null;
}
